import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SendHorizonal, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { useProduct } from "@/context/ProductContext";

type Msg = {
  id: string;
  direction: "inbound" | "outbound";
  content: string;
  created_at: string;
};

const SUGGESTIONS = [
  "What emails need my attention today?",
  "Did anyone get back to me recently?",
  "Summarize my important emails from today",
];

export default function WebChat() {
  const { product_name } = useProduct();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/web-chat/messages");
      setMessages(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendText = async (value: string) => {
    if (!value.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post("/web-chat/send", { text: value });
      setMessages((m) => [...m, r.data.inbound, ...(r.data.outbound ? [r.data.outbound] : [])]);
      setText("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendText(text);
  };

  return (
    <div data-testid="web-chat-page">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Chat with {product_name}</h1>
        <p className="text-sm text-muted-foreground">Ask about your email, tasks, and more.</p>
      </div>

      <div className="rounded-3xl border border-border bg-card">
        <div className="max-h-[58vh] min-h-[320px] space-y-3 overflow-y-auto p-4" data-testid="web-chat-messages">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <p className="text-sm text-muted-foreground">Start a conversation — try one of these:</p>
              <div className="flex flex-col gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid="web-chat-suggestion"
                    onClick={() => sendText(s)}
                    className="rounded-full border border-border bg-secondary/40 px-4 py-2 text-sm transition-colors hover:bg-secondary"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`web-chat-bubble-${m.direction}`}
                  className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.direction === "inbound"
                      ? "rounded-br-md bg-primary text-primary-foreground"
                      : "rounded-bl-md bg-secondary text-secondary-foreground"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  <div
                    className={`mt-1 text-[10px] ${
                      m.direction === "inbound" ? "text-primary-foreground/70" : "text-muted-foreground"
                    }`}
                  >
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start" data-testid="web-chat-typing">
              <div className="rounded-2xl rounded-bl-md bg-secondary px-4 py-2.5 text-sm text-muted-foreground">
                {product_name} is thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-border p-3">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="web-chat-input"
            placeholder={`Message ${product_name}…`}
            className="rounded-full"
            disabled={sending}
          />
          <Button type="submit" size="icon" className="rounded-full" disabled={sending || !text.trim()} data-testid="web-chat-send">
            <SendHorizonal className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Developer tool: the phone-gated <Link to="/simulator" className="text-primary" data-testid="link-sms-simulator">SMS simulator</Link>.
      </p>
    </div>
  );
}
